import { z } from "zod";

// Phase 7 typed configSchema for the `size-and-yield` template (D-01..D-04).
// Replaces the Phase 01.1 z.unknown() placeholder in TEMPLATE_CATALOG so that
// Phase 6's validateRecipe Pass 2A enforces the per-widget config shape end-to-end.
// (Plan 05 swaps this schema into TEMPLATE_CATALOG; this file ships pure schema.)
//
// PITFALLS §C1 (recipe-specific overfitting): NO per-step `metadata` escape
// hatch is permitted on this template. Rich per-step data (recipe-defined
// attributes such as per-size hydration adjustments) flows through recipe-level
// OptionCategory[] declared at the recipe level per Phase 01.1 D-16..D-19.
// Adding a `metadata` field here would re-introduce the closed-enum overfit the
// project pivoted away from.
//
// D-02 (no expression mini-language): `yieldDisplay.perStepFormula` is an
// opaque template-string. The schema validates length only — it does NOT
// validate substitution tokens. The renderer (Plan 08) parses {value}/{units}
// at render time and falls back to "Yield: —" when an unknown token appears.
//
// isolatedDeclarations + verbatimModuleSyntax + exactOptionalPropertyTypes
// (Phase 01.1 D-10/D-15 + Phase 6 D-09): export the interfaces explicitly and
// annotate the const with z.ZodType<SizeAndYieldConfig> so tsc can emit
// declarations without reading across module boundaries (no TS9013).

export interface SizeAndYieldStep {
  id: string;
  label: string;
  multiplier: number;
  min?: number | undefined;
  max?: number | undefined;
}

export interface SizeAndYieldYieldDisplay {
  perStepFormula: string;
  unitsField: string;
  baseValue: number;
}

export interface SizeAndYieldConfig {
  steps: ReadonlyArray<SizeAndYieldStep>;
  yieldDisplay: SizeAndYieldYieldDisplay;
  defaultStepId?: string | undefined;
}

// D-04: multiplier is z.number().positive().finite() — recipes may reasonably
// declare 0.25× / 0.5× / 24× steps; the engine deliberately does NOT impose a
// global multiplier cap. Per-step `min` is nonnegative (a recipe may declare
// a min-batch of 0.5×); per-step `max` is positive (a max of 0 is meaningless).
// superRefine enforces min <= max when both are provided — a step with
// `{ min: 5, max: 2 }` satisfies the individual bounds but is nonsense and
// propagates through Phase 8 Author UI + Phase 11 AI extraction without any
// signal absent this check. Mirrors the cardinality superRefine pattern in
// option-set.ts:46-52.
const _stepSchema = z
  .strictObject({
    id: z
      .string()
      .min(1)
      .max(100)
      .regex(/^[a-z][a-z0-9-]*$/, "kebab-case id required"),
    label: z.string().min(1).max(200),
    multiplier: z.number().positive().finite(),
    min: z.number().nonnegative().optional(),
    max: z.number().positive().optional(),
  })
  .superRefine((step, ctx) => {
    if (step.min !== undefined && step.max !== undefined && step.max < step.min) {
      ctx.addIssue({
        code: "custom",
        path: ["max"],
        message: "step.max must be >= step.min",
      });
    }
  });

// D-02: perStepFormula is an opaque template-string; renderer-side substitution
// validation is intentionally not the schema's responsibility. baseValue is
// nonnegative (a recipe may legitimately declare a baseValue of 0 for
// optional-output items) and finite (NaN / Infinity would crash the renderer).
const _yieldDisplaySchema = z.strictObject({
  perStepFormula: z.string().min(1).max(500),
  unitsField: z.string().min(1).max(50),
  baseValue: z.number().nonnegative().finite(),
});

// Top-level schema: steps.min(1) — a size-and-yield with zero steps is
// meaningless; steps.max(20) — bounds recipe complexity (UI grid layout
// breaks past ~20 size options). The superRefine catches the
// "defaultStepId references a step that doesn't exist" case with a precise
// `path: ["defaultStepId"]` — mirroring the cardinality superRefine pattern
// in option-set.ts.
export const SizeAndYieldConfigSchema: z.ZodType<SizeAndYieldConfig> = z
  .strictObject({
    steps: z.array(_stepSchema).min(1).max(20),
    yieldDisplay: _yieldDisplaySchema,
    defaultStepId: z.string().optional(),
  })
  .superRefine((cfg, ctx) => {
    if (cfg.defaultStepId !== undefined && !cfg.steps.some((s) => s.id === cfg.defaultStepId)) {
      ctx.addIssue({
        code: "custom",
        path: ["defaultStepId"],
        message: `defaultStepId "${cfg.defaultStepId}" not found in steps[]`,
      });
    }
  });
